import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, CardHeader, Col, Input, Row } from 'reactstrap'
import { IBoothRecord } from './index'
import { toast } from 'react-toastify'
import DeleteModal from 'Components/Common/DeleteModal'
import BoothNotesModal from './BoothNotesModal'
import SalesResetModal from './SalesResetModal'
import MaintenanceModal from './MaintenanceModal'
import { maintenanceIdsOf, maintenanceCountOf } from './maintenanceIds'
import { useDebouncedCallback } from 'use-debounce'
import { Spinner } from 'reactstrap'
import { ISort } from 'lib/type'
import { useEffect } from 'react'

interface IProds {
  actionOptions: any[]
  boothRecords: IBoothRecord[]
  handleOpenDetails: (arg: string) => void
  setOpenBoothPopup: (arg: boolean) => void
  setEditMode: (arg: boolean) => void
  handleDeleteBooth: (arg: string) => Promise<string>
  handleUpdateBoothNotes: (id: string, notes: string) => Promise<string>
  searchBooth: (arg: string) => void
  fetching: boolean
  sortAction: ISort
  setSortAction: (arg: ISort) => void
  setCanEdit: (arg: boolean) => void
  /** Bakım kaydedildikten sonra listeyi tazeler (rozet güncel kalsın). */
  refreshBooths?: () => void
}

const BoothsTable: React.FC<IProds> = ({
  boothRecords,
  handleOpenDetails,
  setOpenBoothPopup,
  setEditMode,
  actionOptions,
  handleDeleteBooth,
  handleUpdateBoothNotes,
  searchBooth,
  fetching,
  sortAction,
  setSortAction,
  setCanEdit,
  refreshBooths
}) => {
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  // Kabin cirosu sıfırlama (müşteri talebi 2026-08-14) — para verisi olduğu için
  // onay modalı, basmadan ÖNCE kaç kayıt/ne tutar gizleneceğini gösterir.
  const [salesResetBooth, setSalesResetBooth] = useState<IBoothRecord | null>(null)
  // Bakım modu (müşteri talebi 2026-08-24): kabin bazlı, hizmet seçmeli.
  // 🔴 ActiveAction.isActive KULLANILMAZ — o global, tüm kabinleri etkiler.
  const [maintenanceBooth, setMaintenanceBooth] = useState<IBoothRecord | null>(null)
  const [boothSelectedId, setBoothSelectedId] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState('')

  // Booth notları modalı state
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false)
  const [notesBooth, setNotesBooth] = useState<{ id?: string; name?: string; notes: string }>({
    notes: ''
  })
  // 🔒 2026-08-17: varsayilan FAIL-CLOSED olmali. Eskiden `true` ile
  // basliyordu; asagidaki useEffect yalnizca yetki listesinde BU sayfa
  // bulunursa deger yaziyor. Yani operatorun bu sayfada hic yetkisi yoksa
  // (ya da oturum verisi bozuksa) baslangic degeri KALIYOR ve tum
  // Ekle/Duzenle/Sil dugmeleri ACIK gorunuyordu.
  // Adminler asagidaki `userType === '1'` daliyla ayrica ele aliniyor.
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canEdit: false,
    canDelete: false
  })

  // "ID'yi Unut": admin panelde booth _id'sini maskelemek için toggle set.
  // sessionStorage'da persisted — tarayıcı kapanınca sıfırlanır, kalıcı maskelemeden kaçınmak için.
  const FORGOTTEN_IDS_KEY = 'forgottenBoothIds'
  const [forgottenIds, setForgottenIds] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set()
    try {
      const raw = sessionStorage.getItem(FORGOTTEN_IDS_KEY)
      return new Set<string>(raw ? JSON.parse(raw) : [])
    } catch {
      return new Set()
    }
  })

  const persistForgottenIds = (next: Set<string>) => {
    if (typeof window === 'undefined') return
    try {
      sessionStorage.setItem(FORGOTTEN_IDS_KEY, JSON.stringify(Array.from(next)))
    } catch {
      /* storage dolu vs. — sessiz geç */
    }
  }

  const toggleForgetId = (id: string) => {
    setForgottenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      persistForgottenIds(next)
      return next
    })
  }

  const maskBoothId = (id: string) => {
    if (!id || id.length < 6) return '••••'
    return `••••••••••••••••••${id.slice(-4)}`
  }

  const handleAddBooth = () => {
    setEditMode(false)
    setOpenBoothPopup(true)
  }

  const handleEditBooth = (boothId: string | undefined) => {
    const id = boothId as string
    setEditMode(true)
    // ⚠️ Modalı BURADA AÇMA. `handleOpenDetails` kabini listede bulursa zaten
    // açıyor; bulamazsa açmıyor. Burada da açmak, kayıt bulunamadığında modalın
    // "düzenleme" başlığıyla ama BOŞ kayıtla açılmasına yol açıyordu — o durumda
    // BoothModal "yeni kabin" dalına düşüp VARSAYILAN hizmet listesini yüklüyor.
    // Yani kullanıcı düzenlediğini sandığı kabine farkında olmadan başka bir
    // hizmet kümesi yazabiliyordu (2026-08-28 hizmet kaybının muhtemel yolu).
    handleOpenDetails(id)
  }

  const handleDeleteBoothRecord = async (boothId: string | undefined) => {
    const id = boothId as string
    const res = handleDeleteBooth(id)

    const message = await res
    if (message === 'SUCCESSFULL') {
      toast.success('Deleted a booth successfully')
    }
  }

  const handleShowModalDelete = (boothId: string | undefined) => {
    const id = boothId as string
    setShowModalDelete(true)
    setBoothSelectedId(id)
  }

  const handleOpenNotes = (boothRecord: IBoothRecord) => {
    setNotesBooth({
      id: boothRecord._id,
      name: boothRecord.name,
      notes: boothRecord.notes || ''
    })
    setShowNotesModal(true)
  }

  const handleDelete = () => {
    handleDeleteBoothRecord(boothSelectedId)
    setShowModalDelete(false)
  }

  const showPaymentMethods = (boothRecord: any) => {
    let methods: string[] = []
    const pm = boothRecord.paymentMethods
    if (pm) {
      if (pm.cash) methods.push('Cash')
      if (pm.creditCard) methods.push('Credit Card')
    } else {
      if (boothRecord.isCash) methods.push('Cash')
      if (boothRecord.isCreditCard) methods.push('Credit Card')
    }
    return methods.join(', ')
  }

  const showActiveActions = (boothRecord: IBoothRecord) => {
    if (boothRecord.activeActions && Array.isArray(boothRecord.activeActions)) {
      return boothRecord.activeActions.map((action: any) => action.label || action.name).join(', ')
    }
    return ''
  }

  const renderHardwareIndicator = (
    connected: boolean | undefined,
    label: string,
    tooltip?: string | null
  ) => {
    if (connected === undefined || connected === null) {
      return (
        <span
          title={tooltip || 'Veri yok'}
          style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: '4px',
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            fontWeight: '600',
            fontSize: '11px',
            border: '1px solid #ced4da'
          }}
        >
          {label}: N/A
        </span>
      )
    }
    const color = connected ? '#28a745' : '#dc3545'
    const bg = connected ? '#d4edda' : '#f8d7da'
    const text = connected ? 'Aktif' : 'Kapalı'
    return (
      <span
        title={tooltip || ''}
        style={{
          display: 'inline-block',
          padding: '3px 10px',
          borderRadius: '4px',
          backgroundColor: bg,
          color: color,
          fontWeight: '600',
          fontSize: '11px',
          border: `1px solid ${color}`
        }}
      >
        {text}
      </span>
    )
  }

  // ── Kabin KİMLİK durumu (2026-08-17) ───────────────────────────────────
  // Müşteri: "Id devre dışı kaldığında panelde gösterilebilir mi?"
  // Kabin panelden pasife alındığında backend 403 dönüyor ama sahada bunun
  // hiçbir izi görünmüyordu; kabin sebebi söylenmeden çalışmıyordu.
  //
  // 🔑 Bu kolon BAĞLANTI durumundan (Status) farklı bir soruya cevap verir:
  // "Status" kabinin AYAKTA olup olmadığını, bu kolon kabinin KABUL EDİLİP
  // edilmediğini gösterir. Kabin ayakta olup kimliği reddediliyor olabilir.
  const AUTH_FAILURE_LABEL: Record<string, string> = {
    'booth-inactive': 'Kabin panelden devre dışı bırakılmış',
    'booth-not-found': 'Kabin kaydı bulunamadı',
    'bad-secret': 'Kabin gizli anahtarı uyuşmuyor'
  }

  const formatDateTime = (value?: string | null) => {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.getTime())) return null
    return d.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const renderAuthStatus = (boothRecord: IBoothRecord) => {
    const auth = boothRecord.authStatus
    const badge = (color: string, bg: string, text: string, tooltip: string) => (
      <span
        title={tooltip}
        style={{
          display: 'inline-block',
          padding: '3px 10px',
          borderRadius: '4px',
          backgroundColor: bg,
          color,
          fontWeight: '600',
          fontSize: '11px',
          border: `1px solid ${color}`
        }}
      >
        {text}
      </span>
    )

    // 1) Yönetici kararı — en belirleyici durum. Kabin bu haldeyken hiçbir
    //    işlem yapamaz; sahaya gitmeye gerek yok, buradan geri açılabilir.
    if (boothRecord.isActive === false) {
      return badge(
        '#dc3545',
        '#f8d7da',
        'Devre dışı',
        'Kabin pasife alınmış. Bu satırdaki "Is Active" kutusu işaretlendiğinde kabin kendiliğinden çalışmaya başlar.'
      )
    }

    // 2) Kayıtlı kimlik hatası — kabin aktif görünüyor ama reddedilmiş.
    const failedAt = formatDateTime(auth?.lastAuthFailureAt)
    if (failedAt) {
      const reason = auth?.lastAuthFailureReason || ''
      return badge(
        '#b26a00',
        '#fff3e0',
        'Kimlik hatası',
        `${AUTH_FAILURE_LABEL[reason] || reason || 'Bilinmeyen sebep'} — ${failedAt}`
      )
    }

    // 3) Normal. Kabin uzun ömürlü belge kullandığı için "son doğrulama"
    //    seyrek tazelenir; CANLILIK ölçüsü bu değil, "Status" kolonudur.
    const okAt = formatDateTime(auth?.lastAuthAt)
    return badge(
      '#28a745',
      '#d4edda',
      'Aktif',
      okAt
        ? `Son kimlik doğrulaması: ${okAt}`
        : 'Kimlik sorunu kaydedilmemiş. (Kabin bu sürüm yayına alınmadan önce giriş yaptıysa tarih boş görünür.)'
    )
  }

  // Heartbeat interval is 1 minute, so 3 missed heartbeats = 3 minutes
  const HEARTBEAT_TIMEOUT_MS = 3 * 60 * 1000 // 3 minutes

  const renderConnectionStatus = (
    status: number | undefined,
    lastHeartbeat: string | undefined
  ) => {
    const statusConfig: Record<number, { color: string; bg: string; text: string; icon: string }> =
      {
        0: {
          color: '#dc3545',
          bg: '#f8d7da',
          text: 'Offline',
          icon: 'mdi-close-circle'
        },
        1: {
          color: '#28a745',
          bg: '#d4edda',
          text: 'Online',
          icon: 'mdi-check-circle'
        },
        2: {
          color: '#ff9800',
          bg: '#fff3e0',
          text: 'Busy',
          icon: 'mdi-clock-outline'
        }
      }

    // Check if 3 missed heartbeats (3+ minutes since last heartbeat)
    let effectiveStatus = status ?? 0
    if (lastHeartbeat && (status === 1 || status === 2)) {
      const lastHeartbeatDate = new Date(lastHeartbeat)
      const now = new Date()
      const diffMs = now.getTime() - lastHeartbeatDate.getTime()

      // If more than 3 minutes have passed, consider the booth offline
      if (diffMs > HEARTBEAT_TIMEOUT_MS) {
        effectiveStatus = 0 // OFFLINE
      }
    }

    const config = statusConfig[effectiveStatus] || statusConfig[0]

    return (
      <span
        style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: '4px',
          backgroundColor: config.bg,
          color: config.color,
          fontWeight: '600',
          fontSize: '12px',
          border: `1px solid ${config.color}`
        }}
      >
        <i className={`mdi ${config.icon}`} style={{ marginRight: '5px' }}></i>
        {config.text}
      </span>
    )
  }

  const formatLastOnline = (
    lastOnline: string | undefined,
    connectionStatus: number | undefined
  ) => {
    if (connectionStatus === 1) {
      return (
        <span
          style={{
            color: '#28a745',
            fontWeight: '600',
            fontSize: '12px'
          }}
        >
          Online Now
        </span>
      )
    }

    if (!lastOnline) {
      return (
        <span
          style={{
            color: '#6c757d',
            fontSize: '12px'
          }}
        >
          Never
        </span>
      )
    }

    const lastOnlineDate = new Date(lastOnline)
    const now = new Date()

    // Calculate difference in milliseconds using UTC time
    const diffMs = now.getTime() - lastOnlineDate.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    let timeText = ''
    if (diffMins < 1) {
      timeText = 'Just now'
    } else if (diffMins < 60) {
      timeText = `${diffMins}m ago`
    } else if (diffHours < 24) {
      timeText = `${diffHours}h ago`
    } else if (diffDays < 7) {
      timeText = `${diffDays}d ago`
    } else {
      timeText = lastOnlineDate.toLocaleDateString()
    }

    return (
      <span
        style={{
          color: '#6c757d',
          fontSize: '12px'
        }}
      >
        {timeText}
      </span>
    )
  }

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    searchBooth(searchTerm)
  }, 1500)

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  const handleActionSort = (sortBy: string) => {
    if (sortAction.sortBy === sortBy) {
      setSortAction({
        ...sortAction,
        sortDirection: sortAction.sortDirection === 'DESC' ? 'ASC' : 'DESC'
      })
    } else {
      setSortAction({
        sortDirection: 'DESC',
        sortBy: sortBy
      })
    }
  }

  useEffect(() => {
    const userType = sessionStorage.getItem('userType')

    // Type 1 = Admin - full access to everything
    if (userType === '1') {
      setPermissions({
        canCreate: true,
        canEdit: true,
        canDelete: true
      })
      setCanEdit(true)
      return
    }

    // Type 2 = Regular user - use role-based permissions
    const userRoles = sessionStorage.getItem('userRoles')
      ? JSON.parse(atob(sessionStorage.getItem('userRoles')!))
      : null
    if (userRoles) {
      userRoles.forEach((role: any) => {
        if (role.page === 'Booths') {
          setPermissions({
            canCreate: role.canCreate,
            canEdit: role.canEdit,
            canDelete: role.canDelete
          })
          setCanEdit(role.canEdit)
        }
      })
    }
  }, [setCanEdit])

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex justify-content-between'>
              <div className='search-box d-inline-block col-8 d-flex align-items-center'>
                <input
                  id='search-bar-booth'
                  className='form-control search'
                  placeholder='Search booths'
                  onChange={(e) => handleInputChange(e)}
                  value={query}
                />
                <i className='bx bx-search-alt search-icon'></i>
              </div>
              {permissions.canCreate && (
                <Button className='p-2' color='primary' onClick={(e) => handleAddBooth()}>
                  <div className='flex align-items-center'>
                    {fetching ? (
                      <Spinner size='sm'> Loading... </Spinner>
                    ) : (
                      <>
                        <i className='mdi mdi-plus'></i>
                        <span className='fw-semibold ms-1'>Add Booth</span>
                      </>
                    )}
                  </div>
                </Button>
              )}
            </CardHeader>
            <CardBody>
              <div className='table-responsive table-card' style={{ overflowX: 'auto' }}>
                <table
                  className='table align-middle table-nowrap table-striped-columns mb-0'
                  style={{ minWidth: '1600px' }}
                >
                  <thead className='table-light'>
                    <tr>
                      <th
                        scope='col'
                        onClick={() => handleActionSort('_id')}
                        style={{ minWidth: '70px', cursor: 'pointer' }}
                      >
                        <div className='w-100 d-flex justify-content-between'>
                          <span>ID</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === '_id' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== '_id' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th
                        scope='col'
                        onClick={() => handleActionSort('name')}
                        style={{ minWidth: '120px', cursor: 'pointer' }}
                      >
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Name</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'name' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'name' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' style={{ minWidth: '130px' }}>
                        Status
                      </th>
                      {/* Bağlantı durumunun HEMEN yanında: kabin ayakta mı,
                          ayakta ise kimliği kabul ediliyor mu — iki ayrı soru. */}
                      <th scope='col' style={{ minWidth: '120px' }}>
                        ID Status
                      </th>
                      <th scope='col' style={{ minWidth: '110px' }}>
                        Last Online
                      </th>
                      <th scope='col' style={{ minWidth: '120px' }}>
                        Location
                      </th>
                      <th scope='col' style={{ minWidth: '150px' }}>
                        Active Actions
                      </th>
                      <th scope='col' style={{ minWidth: '150px' }}>
                        Payment Methods
                      </th>
                      <th scope='col' style={{ minWidth: '100px' }}>
                        Currency
                      </th>
                      <th scope='col' style={{ minWidth: '100px' }}>
                        Language
                      </th>
                      <th
                        scope='col'
                        style={{ minWidth: '120px' }}
                      >
                        <span>IP Address</span>
                      </th>
                      <th scope='col' style={{ minWidth: '80px', textAlign: 'center' }}>
                        Printer
                      </th>
                      <th scope='col' style={{ minWidth: '100px', textAlign: 'center' }}>
                        Printer HW
                      </th>
                      <th scope='col' style={{ minWidth: '100px', textAlign: 'center' }}>
                        Camera HW
                      </th>
                      <th scope='col' style={{ minWidth: '100px', textAlign: 'center' }}>
                        POS HW
                      </th>
                      <th scope='col' style={{ minWidth: '80px', textAlign: 'center' }}>
                        Active
                      </th>
                      <th
                        scope='col'
                        style={{
                          width: '230px',
                          minWidth: '230px',
                          position: 'sticky',
                          right: 0,
                          backgroundColor: '#f3f6f9',
                          zIndex: 1
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {boothRecords.map((boothRecord, index) => {
                      return (
                        <tr key={index}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Link
                                to='#'
                                className='fw-medium'
                                style={{
                                  fontFamily: forgottenIds.has(boothRecord._id as string)
                                    ? 'inherit'
                                    : 'monospace',
                                  letterSpacing: forgottenIds.has(boothRecord._id as string) ? '2px' : 'normal'
                                }}
                                title={forgottenIds.has(boothRecord._id as string) ? 'ID gizli' : boothRecord._id}
                              >
                                {forgottenIds.has(boothRecord._id as string)
                                  ? maskBoothId(boothRecord._id as string)
                                  : boothRecord._id}
                              </Link>
                              <button
                                type='button'
                                className='btn btn-sm btn-link p-0'
                                style={{
                                  fontSize: '11px',
                                  textDecoration: 'none',
                                  color: forgottenIds.has(boothRecord._id as string) ? '#0d6efd' : '#6c757d',
                                  whiteSpace: 'nowrap'
                                }}
                                onClick={() => toggleForgetId(boothRecord._id as string)}
                                title={forgottenIds.has(boothRecord._id as string) ? 'ID göster' : 'ID gizle'}
                              >
                                {forgottenIds.has(boothRecord._id as string) ? 'Göster' : "ID'yi Unut"}
                              </button>
                            </div>
                          </td>
                          <td>
                            {boothRecord.name}
                            {/* Bakım rozeti (2026-08-24): kabin kapalıysa sebebi
                                listeden görünsün — aksi halde "neden çalışmıyor"
                                sorusu için kabine gidilir. */}
                            {maintenanceCountOf(boothRecord) > 0 && (
                              <span
                                className='badge bg-warning-subtle text-warning ms-2'
                                title={`${maintenanceCountOf(boothRecord)} hizmet bakımda`}
                              >
                                <i className='mdi mdi-wrench-outline me-1'></i>
                                Bakımda ({maintenanceCountOf(boothRecord)})
                              </span>
                            )}
                          </td>
                          <td>
                            {renderConnectionStatus(
                              boothRecord.connectionStatus?.status,
                              boothRecord.connectionStatus?.lastHeartbeat
                            )}
                          </td>
                          <td>{renderAuthStatus(boothRecord)}</td>
                          <td>
                            {formatLastOnline(
                              boothRecord.connectionStatus?.lastHeartbeat,
                              boothRecord.connectionStatus?.status
                            )}
                          </td>
                          <td>{boothRecord.location?.name}</td>
                          <td>{showActiveActions(boothRecord)}</td>
                          <td>{showPaymentMethods(boothRecord)}</td>
                          <td>{typeof boothRecord.currency === 'string' ? boothRecord.currency : boothRecord.currency?.name || '-'}</td>
                          <td>{typeof boothRecord.language === 'string' ? boothRecord.language : boothRecord.language?.name || '-'}</td>
                          <td>{boothRecord.connectionStatus?.ip || '-'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <Input
                              checked={boothRecord.isPrinterActive || false}
                              className='form-check-input code-switcher'
                              type='checkbox'
                              disabled
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {renderHardwareIndicator(
                              boothRecord.hardwareStatus?.printer?.connected,
                              'Printer',
                              boothRecord.hardwareStatus?.printer?.error || boothRecord.hardwareStatus?.printer?.name
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {renderHardwareIndicator(
                              boothRecord.hardwareStatus?.camera?.connected,
                              'Camera',
                              boothRecord.hardwareStatus?.camera?.type
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {renderHardwareIndicator(
                              boothRecord.hardwareStatus?.pos?.connected,
                              'POS',
                              boothRecord.hardwareStatus?.pos?.error
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <Input
                              checked={boothRecord.isActive || false}
                              className='form-check-input code-switcher'
                              type='checkbox'
                              disabled
                            />
                          </td>
                          <td
                            style={{
                              position: 'sticky',
                              right: 0,
                              backgroundColor: '#fff',
                              zIndex: 1
                            }}
                          >
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              onClick={(e) => handleEditBooth(boothRecord?._id)}
                            >
                              Details
                            </button>
                            <button
                              type='button'
                              className='btn btn-sm btn-soft-info ms-2'
                              onClick={() => handleOpenNotes(boothRecord)}
                              title="Bu booth'a ait notlar"
                            >
                              <i className='mdi mdi-note-text-outline'></i>
                              <span className='ms-1'>Not</span>
                            </button>
                            {permissions.canEdit && (
                              <button
                                type='button'
                                className={`btn btn-sm ms-2 ${
                                  maintenanceCountOf(boothRecord) > 0
                                    ? 'btn-warning'
                                    : 'btn-soft-warning'
                                }`}
                                onClick={() => setMaintenanceBooth(boothRecord)}
                                title='Bu kabinde seçilen hizmetleri bakıma al (kabin bazlı, diğer kabinler etkilenmez)'
                              >
                                <i className='mdi mdi-wrench-outline'></i>
                                <span className='ms-1'>
                                  {maintenanceCountOf(boothRecord) > 0
                                    ? 'Bakımda'
                                    : 'Bakıma Al'}
                                </span>
                              </button>
                            )}
                            {permissions.canDelete && (
                              <button
                                type='button'
                                className='btn btn-sm btn-soft-danger ms-2'
                                onClick={() => setSalesResetBooth(boothRecord)}
                                title='Bu kabinin cirosunu sıfırla (geri alınabilir)'
                              >
                                <i className='mdi mdi-cash-remove'></i>
                                <span className='ms-1'>Reset Revenue</span>
                              </button>
                            )}
                            {permissions.canDelete && (
                              <button
                                type='button'
                                className='btn btn-sm btn-danger ms-2'
                                onClick={(e) => handleShowModalDelete(boothRecord?._id)}
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <MaintenanceModal
        show={!!maintenanceBooth}
        boothId={maintenanceBooth?._id ?? null}
        boothName={maintenanceBooth?.name ?? ''}
        services={(maintenanceBooth?.activeActions ?? []).map((a: any) => ({
          _id: String(a?._id ?? a),
          label: a?.label,
          name: a?.name
        }))}
        initialSelected={maintenanceIdsOf(maintenanceBooth)}
        onClose={() => setMaintenanceBooth(null)}
        onDone={(msg) => {
          toast.success(msg)
          refreshBooths?.()
        }}
      />
      <SalesResetModal
        show={!!salesResetBooth}
        boothId={salesResetBooth?._id ?? null}
        boothName={salesResetBooth?.name ?? ''}
        onClose={() => setSalesResetBooth(null)}
        onDone={(msg) => toast.success(msg)}
      />
      <DeleteModal
        show={showModalDelete}
        onDeleteClick={() => handleDelete()}
        onCloseClick={() => setShowModalDelete(false)}
      />
      <BoothNotesModal
        isOpen={showNotesModal}
        setOpenPopup={setShowNotesModal}
        boothId={notesBooth.id}
        boothName={notesBooth.name}
        currentNotes={notesBooth.notes}
        canEdit={permissions.canEdit}
        handleUpdateBoothNotes={handleUpdateBoothNotes}
      />
    </React.Fragment>
  )
}
export default BoothsTable
